import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 28, className = "", ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: EASE, delay }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

export const MaskLines = ({ lines, className = "", delay = 0, as: Tag = "h1", inView = false }) => {
  const anim = inView
    ? { initial: "hidden", whileInView: "show", viewport: { once: true, amount: 0.3 } }
    : { initial: "hidden", animate: "show" };
  return (
    <Tag className={className}>
      <motion.span className="block" {...anim} transition={{ staggerChildren: 0.11, delayChildren: delay }}>
        {lines.map((line, i) => (
          <span key={i} className="block overflow-hidden pb-[0.08em]">
            <motion.span
              className="block"
              variants={{ hidden: { y: "105%" }, show: { y: "0%", transition: { duration: 1.05, ease: EASE } } }}
            >
              {line}
            </motion.span>
          </span>
        ))}
      </motion.span>
    </Tag>
  );
};

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" data-testid="breadcrumbs">
    <ol className="flex flex-wrap items-center gap-1.5 text-[13px] text-ink-soft">
      {items.map((it, i) => (
        <li key={it.to} className="flex items-center gap-1.5">
          {i < items.length - 1 ? (
            <Link to={it.to} className="link-u font-medium text-slateb">{it.label}</Link>
          ) : (
            <span aria-current="page" className="font-medium text-ink">{it.label}</span>
          )}
          {i < items.length - 1 && <ChevronRight size={13} />}
        </li>
      ))}
    </ol>
  </nav>
);

export const SectionHead = ({ eyebrow, title, desc, align = "left", className = "" }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""} ${className}`}>
    <Reveal><span className="eyebrow">{eyebrow}</span></Reveal>
    <Reveal delay={0.05}><h2 className="mt-5 font-display text-3xl font-bold leading-[1.08] sm:text-4xl lg:text-[52px]">{title}</h2></Reveal>
    {desc && <Reveal delay={0.1}><p className="mt-6 text-base leading-relaxed text-ink md:text-lg">{desc}</p></Reveal>}
  </div>
);

export const PageHero = ({ crumbs, eyebrow, lines, desc, children }) => (
  <section className="relative overflow-hidden bg-ice">
    <div className="grid-lines absolute inset-0 opacity-50 [mask-image:linear-gradient(to_bottom,black,transparent)]" aria-hidden="true" />
    <div className="wrap relative pb-16 pt-10 sm:pb-24 sm:pt-14">
      <Breadcrumbs items={crumbs} />
      <span className="eyebrow mt-10 !flex">{eyebrow}</span>
      <MaskLines lines={lines} className="mt-5 max-w-5xl font-display text-4xl font-extrabold leading-[1.02] sm:text-5xl lg:text-[76px]" />
      {desc && <Reveal delay={0.3}><p className="mt-8 max-w-2xl text-base leading-relaxed text-ink md:text-lg">{desc}</p></Reveal>}
      {children}
    </div>
  </section>
);
